import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Log warning instead of throwing to prevent app crash
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Missing Supabase environment variables. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file.'
  );
  console.warn('The app will continue to run, but authentication features will not work.');
}

// Create Supabase client with fallback values if env vars are missing
let supabaseInstance: ReturnType<typeof createClient<Database>>;
try {
  supabaseInstance = createClient<Database>(
    supabaseUrl || 'https://placeholder.supabase.co',
    supabaseAnonKey || 'placeholder-key',
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    }
  );
} catch(e) {
  // Create a fallback client to prevent auth from being undefined
  console.error('Failed to create Supabase client, using fallback:', e);
  supabaseInstance = createClient<Database>(
    'https://placeholder.supabase.co',
    'placeholder-key',
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    }
  );
}

// Export supabase - must be done before auth object creation
export const supabase = supabaseInstance;

// Helper to get current user's organization_id from their profile
export async function getCurrentOrganizationId(): Promise<string | null> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (!user) {
    console.warn('No authenticated user found');
    if (userError) {
      console.error('Error getting user:', userError);
    }
    return null;
  }

  // Try using the database function first (if available)
  try {
    const { data: orgId, error: rpcError } = await supabase.rpc('get_user_organization_id');
    if (!rpcError && orgId) {
      return orgId;
    }
    if (rpcError) {
      console.warn('RPC function get_user_organization_id error:', rpcError);
    }
  } catch (error) {
    console.warn('RPC function get_user_organization_id not available or failed, falling back to profile query:', error);
  }

  // Fallback to querying profiles table
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('organization_id')
    .eq('id', user.id)
    .single();

  if (profileError) {
    console.error('Error fetching profile:', profileError);
    // If profile doesn't exist, this might be a new user who needs to complete setup
    if (profileError.code === 'PGRST116') {
      console.warn('User profile not found. User may need to complete account setup.');
    } else if (profileError.code === '42501') {
      console.error('Permission denied. Check RLS policies for profiles table.');
    }
    return null;
  }

  if (!profile) {
    console.warn('Profile query returned no data');
    return null;
  }

  if (!profile.organization_id) {
    console.warn('User profile exists but organization_id is null. User may need to be assigned to an organization.');
  }

  return profile.organization_id ?? null;
}

// Helper to get current user's profile
export async function getCurrentProfile() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }

  return profile;
}

// Get redirect URL based on environment
const getRedirectUrl = (): string => {
  // For browser environments, always use current origin
  // This ensures preview deployments use their correct dynamic URL
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/auth/callback`;
  }
  
  // For server-side rendering, use environment variable or fallback
  const envRedirect = process.env.REACT_APP_SITE_URL;
  if (envRedirect) {
    return `${envRedirect}/auth/callback`;
  }
  
  // Default fallback for server-side rendering
  return 'http://localhost:3000/auth/callback';
};

// Auth helper functions - must be created after supabase is initialized
export const auth = {
  signUp: async (email: string, password: string, metadata?: Record<string, unknown>) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    const redirectTo = getRedirectUrl();
    return supabase.auth.signUp({
      email,
      password,
      options: { 
        data: metadata,
        emailRedirectTo: redirectTo,
      },
    });
  },

  signIn: async (email: string, password: string) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    return supabase.auth.signInWithPassword({ email, password });
  },

  signOut: async () => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    return supabase.auth.signOut();
  },

  getSession: async () => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    return supabase.auth.getSession();
  },

  getUser: async () => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    return supabase.auth.getUser();
  },

  onAuthStateChange: (callback: Parameters<typeof supabase.auth.onAuthStateChange>[0]) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    return supabase.auth.onAuthStateChange(callback);
  },

  resetPassword: async (email: string) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    const redirectTo = getRedirectUrl();
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectTo,
    });
  },

  updatePassword: async (newPassword: string) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    return supabase.auth.updateUser({ password: newPassword });
  },

  signInWithGoogle: async () => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    const redirectTo = getRedirectUrl();
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
  },

  signInWithApple: async () => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    const redirectTo = getRedirectUrl();
    return supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo,
      },
    });
  },
};

export default supabase;
